import { useId } from "react";

import styles from "./LogoV2.module.css";

export type LogoV2Variant = "full" | "mark";

interface Props {
  variant?: LogoV2Variant;
  title?: string;
}

const DIMENSIONS: Record<
  LogoV2Variant,
  { width: number; height: number; viewBox: string }
> = {
  // viewBox recortada a las letras: el trazo NO cuenta para el tamaño (desborda
  // por debajo con overflow visible) → el logo se centra siempre por sus letras.
  full: { width: 95, height: 17, viewBox: "0 0.5 168 30" },
  // "S" recortada igual; misma altura de letra que el wordmark (~17px).
  mark: { width: 15, height: 17, viewBox: "0 0 16.8 19" },
};

export const LogoV2 = ({ variant = "full", title }: Props) => {
  const gradientId = useId();
  const dims = DIMENSIONS[variant];

  return (
    <svg
      className={styles.logo}
      width={dims.width}
      height={dims.height}
      viewBox={dims.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role={title != null ? "img" : undefined}
      aria-label={title}
      aria-hidden={title == null ? true : undefined}
    >
      {variant === "full" ? (
        <>
          <path
            d="M97.4503 41.6433C96.99 41.9246 124.479 27.068 167.974 33.3073C167.974 33.3073 130.59 21.3401 97.4503 41.6433Z"
            fill={`url(#${gradientId})`}
          />
          <path
            d="M13.5525 30.4434C8.10594 30.4434 3.57991 28.858 0 25.6616L4.47489 20.3174C7.36438 22.6443 10.484 23.795 13.8594 23.795C16.0584 23.795 17.158 23.1301 17.158 21.8005V21.7237C17.158 21.11 16.8256 20.6242 16.1607 20.2151C15.4959 19.8059 14.1918 19.3968 12.274 18.9621C10.5096 18.553 9.05205 18.1438 7.90137 17.7347C6.75069 17.3256 5.65114 16.7886 4.5516 16.1237C3.45205 15.4589 2.65936 14.5895 2.12237 13.5667C1.61096 12.5438 1.32968 11.2909 1.32968 9.8589V9.78219C1.32968 7.04612 2.35251 4.82146 4.3726 3.10822C6.39269 1.39498 9.12877 0.525571 12.5297 0.525571C17.2603 0.525571 21.2237 1.80411 24.4457 4.36119L20.4311 10.0123C17.7205 8.12009 15.0356 7.1484 12.3251 7.1484C11.379 7.1484 10.663 7.3274 10.1772 7.65982C9.69132 7.99224 9.43562 8.45251 9.43562 9.01507V9.09178C9.43562 9.75662 9.76804 10.268 10.4584 10.6516C11.1233 11.0352 12.453 11.4443 14.4475 11.879C18.053 12.6717 20.7635 13.7457 22.5534 15.0498C24.3434 16.3795 25.2384 18.3228 25.2384 20.8799V20.9566C25.2384 23.8717 24.19 26.1731 22.0676 27.8607C19.9452 29.5995 17.1068 30.4434 13.5525 30.4434ZM38.6374 30.4689C34.4694 30.4689 31.2475 29.3694 28.9461 27.1703C26.6447 24.9712 25.4941 21.647 25.4941 17.2489V1.01142H33.3443V17.0954C33.3443 19.1411 33.8301 20.6753 34.7763 21.6982C35.7224 22.721 37.0265 23.258 38.6886 23.258C40.3507 23.258 41.6548 22.7721 42.6009 21.7749C43.547 20.7776 44.0329 19.2945 44.0329 17.3V1.01142H51.9342V17.0443C51.9342 21.5703 50.758 24.9201 48.4311 27.1447C46.0785 29.3694 42.8311 30.4689 38.6374 30.4689ZM54.1845 29.932V1.01142H61.6L73.5416 16.3027V1.01142H81.3918V29.932H74.4365L62.0347 14.0525V29.932H54.1845ZM91.5945 14.0781L102.718 28.3466C105.3 27.7073 108.037 27.1192 110.926 26.5822V1.01142H103.076V16.3027L91.1342 1.01142H83.7187V29.932H91.569L91.5945 14.0781ZM129.03 24.0763C132.431 23.7694 135.755 23.5648 138.977 23.437L134.4 19.6525C132.457 21.5447 130.385 22.4909 128.161 22.4909C124.888 22.4909 122.868 20.9566 122.075 17.9137H140.921C140.946 17.4534 140.972 16.7886 140.972 15.8936C140.972 11.4443 139.744 7.78767 137.341 4.8726C134.911 1.95753 131.383 0.5 126.78 0.5C122.663 0.5 119.262 1.90639 116.577 4.74475C113.918 7.58311 112.563 11.0607 112.563 15.2288V15.3311C112.563 19.5247 113.892 22.9511 116.5 25.6361C120.49 24.9968 124.683 24.4854 129.03 24.0763ZM150.51 23.3603V20.8032H153.962H154.038L155.828 23.5904C159.383 23.8205 162.681 24.1785 165.622 24.6644L162.195 19.5758C166.057 17.9137 168 14.9986 168 10.8306V10.7539C168 7.96667 167.156 5.74201 165.468 4.13105C163.448 2.05982 160.354 1.03699 156.161 1.03699H142.659V23.3603C145.37 23.2836 148.004 23.2836 150.51 23.3603ZM150.51 7.9411H155.777C157.158 7.9411 158.232 8.22237 158.999 8.8105C159.766 9.39863 160.15 10.2425 160.15 11.3676V11.4443C160.15 12.5183 159.766 13.3621 158.999 13.9758C158.232 14.5895 157.184 14.8963 155.828 14.8963H150.51V7.9411Z"
            fill="currentColor"
          />
        </>
      ) : (
        <>
          <path
            d="M0.605846 22.0001C0.605846 22.0001 7.0791 16.8144 18.1527 19.8874C18.1787 19.8943 18.2056 19.9012 18.2315 19.9082C18.2315 19.9082 8.68183 18.7245 0.60498 22.0001H0.605846Z"
            fill={`url(#${gradientId})`}
          />
          <path
            d="M9.62416 7.59109C6.95641 6.98585 6.29662 6.6014 6.29662 5.72081V5.6654C6.29662 4.9779 6.92871 4.42807 8.22145 4.42807C9.92635 4.42807 11.8512 5.08786 13.6115 6.32519L16.2793 2.55778C14.189 0.880591 11.6321 0 8.35913 0C3.73883 0 0.906568 2.58463 0.906568 6.15981V6.21523C0.906568 10.1749 4.06873 11.3299 8.1669 12.2651C10.7792 12.8703 11.4122 13.3102 11.4122 14.1076V14.1631C11.4122 14.9882 10.6424 15.4826 9.21201 15.4826C6.98412 15.4826 4.86706 14.6852 2.96994 13.1725L0 16.72C1.11697 17.7201 2.4305 18.4864 3.86439 19.0171C8.47948 17.0923 13.6089 17.7149 15.0211 17.94C16.1589 16.8819 16.8022 15.4255 16.8022 13.6392V13.5838C16.8022 9.98176 14.0522 8.55135 9.62503 7.5885L9.62416 7.59109Z"
            fill="currentColor"
          />
        </>
      )}
      <defs>
        {variant === "full" ? (
          <linearGradient
            id={gradientId}
            x1="97.3572"
            y1="36.0677"
            x2="168"
            y2="34.9617"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#FBC02C" />
            <stop offset="0.3629" stopColor="#EA5C27" />
            <stop offset="0.6" stopColor="#D91D5F" />
            <stop offset="0.9963" stopColor="#901D7E" />
          </linearGradient>
        ) : (
          <linearGradient
            id={gradientId}
            x1="0.605846"
            y1="20.4571"
            x2="18.2324"
            y2="20.4571"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#F4BA29" />
            <stop offset="0.37" stopColor="#E73B34" />
            <stop offset="0.7" stopColor="#D4006C" />
            <stop offset="1" stopColor="#961F81" />
          </linearGradient>
        )}
      </defs>
    </svg>
  );
};
